/**
 * Sesión del **portal del cliente**.
 *
 * Vive en su propio almacenamiento (`edsuite-portal:session`): al ser una
 * aplicación aparte, su sesión no puede pisar la del panel aunque ambas se abran
 * en el mismo navegador. En el servidor la separación es por el `scope` del
 * token: `requireCliente` sólo acepta los del portal y `requireAuth` los rechaza.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { ClientePortal } from './portal.types';

const CLAVE = 'edsuite-portal:session';

interface PortalState {
  cliente: ClientePortal | null;
  token: string | null;
  /** Fecha (ISO) en la que se guardó el token, para diagnóstico. */
  desde: string | null;
  iniciarSesion: (cliente: ClientePortal, token: string) => void;
  actualizarCliente: (cliente: ClientePortal) => void;
  cerrarSesion: () => void;
}

export const usePortalStore = create<PortalState>()(
  persist(
    (set) => ({
      cliente: null,
      token: null,
      desde: null,

      iniciarSesion: (cliente, token) =>
        set({ cliente, token, desde: new Date().toISOString() }),

      actualizarCliente: (cliente) => set({ cliente }),

      cerrarSesion: () => set({ cliente: null, token: null, desde: null }),
    }),
    {
      name: CLAVE,
      storage: createJSONStorage(() => localStorage),
      partialize: (estado) => ({
        cliente: estado.cliente,
        token: estado.token,
        desde: estado.desde,
      }),
    },
  ),
);

export const selectClientePortal = (estado: PortalState): ClientePortal | null => estado.cliente;
export const selectTokenPortal = (estado: PortalState): string | null => estado.token;

/** ¿Hay sesión de cliente? */
export function portalAutenticado(estado: PortalState): boolean {
  return Boolean(estado.token && estado.cliente);
}
